import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from './supabaseClient';
import Sidebar from './Sidebar';
import ProfileMenu from './ProfileMenu';
import './AuthForm.css';
import { FaArrowLeft, FaCheck, FaXmark, FaEye } from 'react-icons/fa6';
import Preloader from './Preloader';

function IDVerificationDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const [verificationLoading, setVerificationLoading] = useState(false);
  const [verificationMsg, setVerificationMsg] = useState('');
  const auditLoggedRef = useRef(false);

  useEffect(() => {
    document.title = "ID Verification Details";
  }, []);

  useEffect(() => {
    const getCurrentUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('*')
          .eq('user_id', user.id)
          .single();
        setCurrentUser(profile);
      }
    };
    getCurrentUser();
    fetchUser();
    // eslint-disable-next-line
  }, [id]);

  // Add audit log for viewing the ID (admin/super admin only)
  useEffect(() => {
    if (!auditLoggedRef.current && currentUser && (currentUser.role === 'admin' || currentUser.role === 'super admin') && id) {
      auditLoggedRef.current = true;
      (async () => {
        await supabase.from('audit_logs').insert([{
          user_id: currentUser.id,
          action: 'view ID',
          target_id: id,
          target_type: 'VerifyID',
          details: { email: user?.email }
        }]);
      })();
    }
  }, [currentUser, id, user]);

  async function fetchUser() {
    setLoading(true);
    setError('');
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', id)
      .single();
    if (error) {
      setError('Failed to load user details');
      setUser(null);
    } else {
      setUser(data);
    }
    setLoading(false);
  }

  // Function to get the proper image URL
  const getImageUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    if (url.includes('id-documents/ids/')) return url;
    const { data: { publicUrl } } = supabase.storage
      .from('id-documents')
      .getPublicUrl(`ids/${url}`);
    return publicUrl;
  };

  if (loading || !currentUser) return <Preloader text="Loading User Details..." />;
  if (error || !user) return <div style={{ color: '#d32f2f', padding: 40 }}>{error || 'User not found.'}</div>;

  // Check if user has permission to access ID verification
  if (currentUser.role !== 'admin' && currentUser.role !== 'super admin') {
    return (
      <div style={{ display: 'flex', minHeight: '100vh', background: '#f4f8f8' }}>
        <Sidebar />
        <div style={{ flex: 1, padding: 32, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff3cd', color: '#856404', border: '1px solid #ffeeba', borderRadius: 8, padding: '32px 48px', fontWeight: 500, fontSize: 20 }}>
            Access denied. Only admin and super admin users can access ID verification.
          </div>
        </div>
      </div>
    );
  }

  const canVerify = currentUser && currentUser.role === 'super admin';
  const avatarUrl = user.avatar_url || 'https://www.svgrepo.com/show/384674/account-avatar-profile-user-11.svg';
  const imageUrl = getImageUrl(user.id_document_url);

  async function handleVerification(action) {
    if (!currentUser || currentUser.role !== 'super admin') {
      alert('Only Super Admins can verify user IDs');
      return;
    }

    setVerificationLoading(true);
    setVerificationMsg('');

    try {
      const newStatus = action === 'approve' ? 'verified' : 'rejected';
      // Only send fields that exist in the table
      const updatePayload = {
        id_verification_status: newStatus,
        id_verified_by: currentUser.id,
        id_verified_at: new Date().toISOString()
      };
      const { error } = await supabase
        .from('profiles')
        .update(updatePayload)
        .eq('id', id);

      if (error) {
        setVerificationMsg('Failed to update verification status.');
        console.error('Supabase update error:', error, 'Payload:', updatePayload, 'ID:', id);
      } else {
        setVerificationMsg(`ID verification ${action === 'approve' ? 'approved' : 'rejected'} successfully!`);
        // Audit log for accept/reject
        await supabase.from('audit_logs').insert([{
          user_id: currentUser.id,
          action: action === 'approve' ? 'accepted ID' : 'rejected ID',
          target_id: id,
          target_type: 'VerifyID',
          details: { email: user?.email }
        }]);
        fetchUser();
      }
    } catch (error) {
      console.error('Verification error:', error);
      setVerificationMsg('An error occurred during verification.');
    }
    
    setVerificationLoading(false);
  }

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f4f8f8' }}>
      <Sidebar />
      <div style={{ flex: 1, padding: 32 }}>
        {/* Top bar */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 32 }}>
          <button
            onClick={() => navigate(-1)}
            style={{
              background: 'none',
              border: 'none',
              color: '#666',
              cursor: 'pointer',
              fontSize: 20,
              display: 'flex',
              alignItems: 'center',
              gap: 8
            }}
          >
            <FaArrowLeft />
          </button>
          <ProfileMenu />
        </div>

        {/* Main Card */}
        <div style={{ background: '#fff', borderRadius: 16, padding: 48, boxShadow: '0 4px 24px rgba(8, 177, 199, 0.10)', width: '100%', maxWidth: '100%', margin: 0, boxSizing: 'border-box' }}>
          {/* Title row */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 32 }}>
            <h1 style={{ fontSize: 28, margin: 0, fontWeight: 800 }}>{user.first_name} {user.last_name}</h1>
            <div style={{ display: 'flex', gap: 24, alignItems: 'center' }}>
              <div>
                <span style={{ color: '#666', marginRight: 8, fontWeight: 500 }}>Status:</span>
                <span style={{
                  display: 'inline-block',
                  padding: '4px 16px',
                  borderRadius: 16,
                  fontWeight: 700,
                  fontSize: 15,
                  background: user.status === 'active' ? '#e6f4ea' : 
                             user.status === 'inactive' ? '#fce8e6' : 
                             user.status === 'banned' ? '#ffebee' : '#f1f3f4',
                  color: user.status === 'active' ? '#1e7e34' : 
                         user.status === 'inactive' ? '#d32f2f' : 
                         user.status === 'banned' ? '#c62828' : '#5f6368',
                  border: '1px solid #eee',
                  minWidth: 70,
                  textAlign: 'center',
                  boxShadow: '0 2px 8px #0001',
                  letterSpacing: 0.5
                }}>
                  {user.status.charAt(0).toUpperCase() + user.status.slice(1)}
                </span>
              </div>
              <div>
                <span style={{ color: '#666', marginRight: 8, fontWeight: 500 }}>Role:</span>
                <span style={{ fontSize: 16, fontWeight: 800, color: '#1976d2', letterSpacing: 0.5 }}>
                  {user.role ? user.role.charAt(0).toUpperCase() + user.role.slice(1) : 'Resident'}
                </span>
              </div>
            </div>
          </div>

          {/* Avatar at the top center */}
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginBottom: 32 }}>
            <img
              src={avatarUrl}
              alt="User avatar"
              style={{
                width: 120,
                height: 120,
                borderRadius: '50%',
                objectFit: 'cover',
                border: '3px solid #e1e8ed',
                boxShadow: '0 4px 12px rgba(8,177,199,0.10)'
              }}
            />
          </div>

          {/* Two-column grid for all labels/values */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '32px 64px',
            marginBottom: 24,
            alignItems: 'start',
            maxWidth: 1000,
            margin: '0 auto 24px auto'
          }}>
            {/* Left column: Email to Member Since */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
              <div>
                <label style={{ color: '#888', marginBottom: 2, fontSize: 15 }}>Email</label>
                <div style={{ fontSize: 16, fontWeight: 500 }}>{user.email}</div>
              </div>
              <div>
                <label style={{ color: '#888', marginBottom: 2, fontSize: 15 }}>Mobile Number</label>
                <div style={{ fontSize: 16, fontWeight: 500 }}>{user.mobile_number || 'N/A'}</div>
              </div>
              <div>
                <label style={{ color: '#888', marginBottom: 2, fontSize: 15 }}>Birth Date</label>
                <div style={{ fontSize: 16, fontWeight: 500 }}>{user.birth_date || 'N/A'}</div>
              </div>
              <div>
                <label style={{ color: '#888', marginBottom: 2, fontSize: 15 }}>Gender</label>
                <div style={{ fontSize: 16, fontWeight: 500 }}>{user.gender || 'N/A'}</div>
              </div>
              <div>
                <label style={{ color: '#888', marginBottom: 2, fontSize: 15 }}>Member Since</label>
                <div style={{ fontSize: 16, fontWeight: 500 }}>{new Date(user.created_at).toLocaleDateString()}</div>
              </div>
            </div>
            {/* Right column: House/Street to ZIP Code */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
              <div>
                <label style={{ color: '#888', marginBottom: 2, fontSize: 15 }}>House/Street</label>
                <div style={{ fontSize: 16, fontWeight: 500 }}>{user.house_street || 'N/A'}</div>
              </div>
              <div>
                <label style={{ color: '#888', marginBottom: 2, fontSize: 15 }}>Subdivision</label>
                <div style={{ fontSize: 16, fontWeight: 500 }}>{user.subdivision || 'N/A'}</div>
              </div>
              <div>
                <label style={{ color: '#888', marginBottom: 2, fontSize: 15 }}>Barangay</label>
                <div style={{ fontSize: 16, fontWeight: 500 }}>{user.barangay || 'N/A'}</div>
              </div>
              <div>
                <label style={{ color: '#888', marginBottom: 2, fontSize: 15 }}>City</label>
                <div style={{ fontSize: 16, fontWeight: 500 }}>{user.city || 'N/A'}</div>
              </div>
              <div>
                <label style={{ color: '#888', marginBottom: 2, fontSize: 15 }}>Province</label>
                <div style={{ fontSize: 16, fontWeight: 500 }}>{user.province || 'N/A'}</div>
              </div>
              <div>
                <label style={{ color: '#888', marginBottom: 2, fontSize: 15 }}>ZIP Code</label>
                <div style={{ fontSize: 16, fontWeight: 500 }}>{user.zip || 'N/A'}</div>
              </div>
            </div>
          </div>

          {/* ID Verification Section */}
          <div style={{ marginTop: 48, paddingTop: 32, borderTop: '2px solid #e1e8ed' }}>
            <h2 style={{ fontSize: 22, marginBottom: 28, color: '#1976d2', fontWeight: 800, letterSpacing: 0.5 }}>ID Verification</h2>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 32 }}>
              {/* ID Status */}
              <div style={{ flex: '1 1 320px', background: '#f8fcfd', borderRadius: 12, padding: 32, boxShadow: '0 2px 8px #08b1c71a', minWidth: 260 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                  <div>
                    <label style={{ color: '#888', marginBottom: 2, fontSize: 15 }}>Verification Status</label>
                    <span style={{
                      display: 'inline-block',
                      padding: '4px 16px',
                      borderRadius: 16,
                      fontWeight: 700,
                      fontSize: 15,
                      background: user.id_verification_status === 'verified' ? '#e6f4ea' : 
                                 user.id_verification_status === 'submitted' ? '#fff3cd' : 
                                 user.id_verification_status === 'rejected' ? '#ffebee' : 
                                 user.id_verification_status === 'pending' ? '#f1f3f4' : '#f1f3f4',
                      color: user.id_verification_status === 'verified' ? '#1e7e34' : 
                             user.id_verification_status === 'submitted' ? '#856404' : 
                             user.id_verification_status === 'rejected' ? '#c62828' : 
                             user.id_verification_status === 'pending' ? '#5f6368' : '#5f6368',
                      border: '1px solid #eee',
                      minWidth: 70,
                      textAlign: 'center',
                      boxShadow: '0 2px 8px #0001',
                      letterSpacing: 0.5
                    }}>
                      {user.id_verification_status ? user.id_verification_status.charAt(0).toUpperCase() + user.id_verification_status.slice(1) : 'Pending'}
                    </span>
                  </div>
                  {user.id_verified_by && (
                    <div>
                      <label style={{ color: '#888', marginBottom: 2, fontSize: 15 }}>Verified By</label>
                      <div style={{ fontSize: 16, fontWeight: 500 }}>{user.id_verified_by}</div>
                    </div>
                  )}
                  {user.id_verified_at && (
                    <div>
                      <label style={{ color: '#888', marginBottom: 2, fontSize: 15 }}>Verified At</label>
                      <div style={{ fontSize: 16, fontWeight: 500 }}>{new Date(user.id_verified_at).toLocaleString()}</div>
                    </div>
                  )}
                </div>
              </div>
              {/* ID Document */}
              <div style={{ flex: '1 1 320px', background: '#f8fcfd', borderRadius: 12, padding: 32, boxShadow: '0 2px 8px #08b1c71a', minWidth: 260 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                  <div>
                    <label style={{ color: '#888', marginBottom: 8, fontSize: 15, display: 'block' }}>ID Document</label>
                    {imageUrl ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                        <img
                          src={imageUrl}
                          alt="ID Document"
                          style={{
                            maxWidth: '100%',
                            maxHeight: 200,
                            borderRadius: 8,
                            border: '1px solid #e1e8ed',
                            objectFit: 'contain',
                            background: '#f8f9fa'
                          }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'block';
                          }}
                        />
                        <div style={{ 
                          display: 'none', 
                          padding: 20, 
                          border: '2px dashed #e1e8ed', 
                          borderRadius: 8, 
                          textAlign: 'center', 
                          color: '#dc3545',
                          background: '#ffebee',
                          fontSize: 14
                        }}>
                          Failed to load image: {imageUrl}
                        </div>
                        <a
                          href={imageUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 8,
                            padding: '8px 16px',
                            background: '#08b1c7',
                            color: '#fff',
                            textDecoration: 'none',
                            borderRadius: 6,
                            fontSize: 14,
                            fontWeight: 500,
                            width: 'fit-content',
                            boxShadow: '0 2px 8px #08b1c71a'
                          }}
                        >
                          <FaEye />
                          View Full Size
                        </a>
                      </div>
                    ) : (
                      <div style={{
                        padding: 40,
                        border: '2px dashed #e1e8ed',
                        borderRadius: 8,
                        textAlign: 'center',
                        color: '#888',
                        background: '#f8f9fa',
                        fontSize: 15
                      }}>
                        No ID document submitted
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Verification Actions and Notes */}
            {canVerify && imageUrl && (user.id_verification_status === 'pending' || user.id_verification_status === 'submitted') && (
              <div style={{ 
                marginTop: 32, 
                padding: 24, 
                background: '#f8f9fa', 
                borderRadius: 12, 
                border: '1px solid #e1e8ed',
                boxShadow: '0 2px 8px #08b1c71a'
              }}>
                <h3 style={{ fontSize: 17, marginBottom: 18, color: '#1976d2', fontWeight: 700 }}>Verification Actions</h3>
                <div style={{ display: 'flex', gap: 16 }}>
                  <button
                    onClick={() => handleVerification('approve')}
                    disabled={verificationLoading}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '12px 28px',
                      background: '#28a745',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 8,
                      fontWeight: 'bold',
                      fontSize: 15,
                      cursor: verificationLoading ? 'not-allowed' : 'pointer',
                      opacity: verificationLoading ? 0.7 : 1,
                      boxShadow: '0 2px 8px #0001'
                    }}
                  >
                    <FaCheck />
                    Approve
                  </button>
                  <button
                    onClick={() => handleVerification('reject')}
                    disabled={verificationLoading}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '12px 28px',
                      background: '#dc3545',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 8,
                      fontWeight: 'bold',
                      fontSize: 15,
                      cursor: verificationLoading ? 'not-allowed' : 'pointer',
                      opacity: verificationLoading ? 0.7 : 1,
                      boxShadow: '0 2px 8px #0001'
                    }}
                  >
                    <FaXmark />
                    Reject
                  </button>
                </div>
                {verificationMsg && (
                  <div style={{ 
                    color: verificationMsg.includes('Failed') || verificationMsg.includes('error') ? '#dc3545' : '#28a745', 
                    marginTop: 16,
                    fontSize: 15
                  }}>
                    {verificationMsg}
                  </div>
                )}
              </div>
            )}

            {canVerify && imageUrl && user.id_verification_status === 'verified' && (
              <div style={{ 
                marginTop: 32, 
                padding: 24, 
                background: '#e6f4ea', 
                borderRadius: 12, 
                border: '1px solid #c8e6c9',
                color: '#1e7e34',
                fontWeight: 600,
                fontSize: 15,
                boxShadow: '0 2px 8px #08b1c71a'
              }}>
                <strong>✓ Verified:</strong> This user's ID has been verified successfully.
              </div>
            )}

            {canVerify && imageUrl && user.id_verification_status === 'rejected' && (
              <div style={{ 
                marginTop: 32, 
                padding: 24, 
                background: '#ffebee', 
                borderRadius: 12, 
                border: '1px solid #ffcdd2',
                color: '#c62828',
                fontWeight: 600,
                fontSize: 15,
                boxShadow: '0 2px 8px #08b1c71a'
              }}>
                <strong>✗ Rejected:</strong> This user's ID verification was rejected.
                <div style={{ marginTop: 16 }}>
                  <button
                    onClick={async () => {
                      setVerificationLoading(true);
                      setVerificationMsg('');
                      try {
                        const updatePayload = {
                          id_verification_status: 'pending',
                          id_verified_by: null,
                          id_verified_at: null
                        };
                        const { error } = await supabase
                          .from('profiles')
                          .update(updatePayload)
                          .eq('id', id);
                        if (error) {
                          setVerificationMsg('Failed to set for reevaluation.');
                        } else {
                          setVerificationMsg('ID set for reevaluation.');
                          // Audit log for reevaluated ID
                          await supabase.from('audit_logs').insert([{
                            user_id: currentUser.id,
                            action: 'reevaluated ID',
                            target_id: id,
                            target_type: 'VerifyID',
                            details: { email: user?.email }
                          }]);
                          fetchUser();
                        }
                      } catch (error) {
                        setVerificationMsg('An error occurred during reevaluation.');
                      }
                      setVerificationLoading(false);
                    }}
                    disabled={verificationLoading}
                    style={{
                      marginTop: 8,
                      background: '#08b1c7',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 8,
                      padding: '10px 24px',
                      fontWeight: 'bold',
                      fontSize: 15,
                      cursor: verificationLoading ? 'not-allowed' : 'pointer',
                      opacity: verificationLoading ? 0.7 : 1,
                      boxShadow: '0 2px 8px #0001'
                    }}
                  >
                    Reevaluate
                  </button>
                  {verificationMsg && (
                    <div style={{ color: verificationMsg.includes('Failed') || verificationMsg.includes('error') ? '#dc3545' : '#28a745', marginTop: 8, fontSize: 15 }}>
                      {verificationMsg}
                    </div>
                  )}
                </div>
              </div>
            )}

            {canVerify && !imageUrl && (
              <div style={{ 
                marginTop: 32, 
                padding: 24, 
                background: '#f8f9fa', 
                borderRadius: 12, 
                border: '1px solid #e1e8ed',
                color: '#666',
                fontWeight: 500,
                fontSize: 15,
                boxShadow: '0 2px 8px #08b1c71a'
              }}>
                <strong>Note:</strong> This user has not submitted an ID document for verification.
              </div>
            )}

            {!canVerify && (
              <div style={{ 
                marginTop: 32, 
                padding: 24, 
                background: '#f8f9fa', 
                borderRadius: 12, 
                border: '1px solid #e1e8ed',
                color: '#666',
                fontWeight: 500,
                fontSize: 15,
                boxShadow: '0 2px 8px #08b1c71a'
              }}>
                <strong>Note:</strong> Only Super Admins can verify user IDs.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default IDVerificationDetails; 